/**
 * Product Variant Image Filtering
 * Filters product images based on selected variant title matching alt text format: #size_Variant Title
 */

(function() {
  'use strict';


  function patchProductImagesForFilteredSlides() {
    const ProductImagesElement = customElements.get('product-images');
    if (!ProductImagesElement) {
      setTimeout(patchProductImagesForFilteredSlides, 100);
      return;
    }
    if (ProductImagesElement.prototype.__variantFilterPatched) return;

    ProductImagesElement.prototype.__variantFilterPatched = true;

    ProductImagesElement.prototype.getVisibleMediaItems = function() {
      const filteredItems = Array.from(this.querySelectorAll('[data-image-id][data-variant-filter-visible="true"]'));
      if (filteredItems.length) return filteredItems;
      return Array.from(this.querySelectorAll('[data-image-id]:not(.media--hidden)'));
    };

    ProductImagesElement.prototype.getNextMediaId = function() {
      const visibleItems = this.getVisibleMediaItems();
      if (!visibleItems.length) return null;

      const currentMedia = this.querySelector('[data-image-id].media--active') || visibleItems[0];
      const currentIndex = Math.max(visibleItems.indexOf(currentMedia), 0);
      const nextMedia = visibleItems[(currentIndex + 1) % visibleItems.length];
      return nextMedia?.getAttribute('data-media-id');
    };

    ProductImagesElement.prototype.getPreviousMediaId = function() {
      const visibleItems = this.getVisibleMediaItems();
      if (!visibleItems.length) return null;

      const currentMedia = this.querySelector('[data-image-id].media--active') || visibleItems[0];
      const currentIndex = Math.max(visibleItems.indexOf(currentMedia), 0);
      const previousIndex = currentIndex - 1 < 0 ? visibleItems.length - 1 : currentIndex - 1;
      const previousMedia = visibleItems[previousIndex];
      return previousMedia?.getAttribute('data-media-id');
    };

    const originalShowMediaOnVariantSelect = ProductImagesElement.prototype.showMediaOnVariantSelect;
    ProductImagesElement.prototype.showMediaOnVariantSelect = function(e) {
      let id = e?.detail?.id;
      if (!id) return;

      const selected = this.querySelector(`[data-media-id="${id}"]`);
      if (selected && selected.classList.contains('media--hidden') && selected.getAttribute('data-variant-filter-visible') !== 'true') {
        const fallback = this.querySelector('[data-image-id][data-variant-filter-visible="true"]')
          || this.querySelector('[data-image-id]:not(.media--hidden)');
        if (fallback) id = fallback.getAttribute('data-media-id');
      }

      if (typeof originalShowMediaOnVariantSelect === 'function') {
        return originalShowMediaOnVariantSelect.call(this, { detail: { id } });
      }

      this.setActiveMedia(id);
    };
  }

  function normalizeVariantValue(value) {
    if (!value) return '';
    const normalized = String(value).replace(/\s+/g, ' ').trim().toLowerCase();
    const segments = normalized.split('/');
    return (segments[segments.length - 1] || normalized).trim();
  }

  // Wait for product-info custom element to be defined
  function waitForProductInfo() {
    if (customElements.get('product-info')) {
      patchProductImagesForFilteredSlides();
      initializeVariantImageFiltering();
    } else {
      setTimeout(waitForProductInfo, 100);
    }
  }

  class ProductVariantImages {
    constructor(productInfo) {
      this.productInfo = productInfo;
      this.allMediaSlides = [];
      this.variantImages = [];
      this.genericImages = [];
      this.activeMediaStabilizerTimers = [];
      
      this.init();
    }

    getMediaList() {
      return this.productInfo && this.productInfo.isConnected
        ? this.productInfo.querySelector('[data-product-media-list]')
        : null;
    }

    isSlideInFilteredSet(slide) {
      return slide?.getAttribute('data-variant-filter-visible') === 'true';
    }

    hasEnoughVariantImageGroups() {
      const uniqueTitles = new Set(
        this.variantImages
          .map(({ variantTitle }) => (variantTitle || '').trim().toLowerCase())
          .filter(Boolean)
      );
      return uniqueTitles.size >= 2;
    }

    getThumbs() {
      return this.productInfo && this.productInfo.isConnected
        ? this.productInfo.querySelector('product-thumbs')
        : null;
    }

    syncFloatingVideo(targetSlide) {
      const productMediaList = this.getMediaList();
      if (!productMediaList || !targetSlide) return;

      const floatingVideo = productMediaList.querySelector('.product__slide .product-videoview:not(.mobile-show)');
      if (!floatingVideo) return;

      const targetContainer = targetSlide.querySelector('.product__photo') || targetSlide;
      if (!targetContainer) return;
      if (floatingVideo.parentElement === targetContainer) return;

      targetContainer.appendChild(floatingVideo);
    }

    isMobileFaderMode() {
      const productImages = this.productInfo && this.productInfo.isConnected
        ? this.productInfo.querySelector('product-images')
        : null;
      if (!productImages) return false;

      const hasMobileFader = productImages.hasAttribute('data-fader-mobile');
      const isMobile = Boolean(
        (theme && theme.isMobile)
        || (typeof window !== 'undefined' && window.matchMedia && window.matchMedia('(max-width: 749px)').matches)
      );

      return hasMobileFader && isMobile;
    }

    init() {
      const productMediaList = this.getMediaList();
      if (!productMediaList) {
        return;
      }

      // Cache all media slides from current DOM
      this.allMediaSlides = Array.from(productMediaList.querySelectorAll('[data-slide]'));
      
      if (this.allMediaSlides.length === 0) {
        return;
      }

      // Separate variant-specific images from generic images
      // Treat data-variant-title="all" as generic so those images show for every variant
      this.allMediaSlides.forEach(slide => {
        const variantTitle = slide.getAttribute('data-variant-title');
        if (variantTitle) {
          const trimmed = variantTitle.trim();
          if (trimmed.toLowerCase() === 'all') {
            this.genericImages.push(slide);
          } else {
            this.variantImages.push({
              slide: slide,
              variantTitle: trimmed
            });
          }
        } else {
          this.genericImages.push(slide);
        }
      });


      // Only enable filtering when there are at least two distinct variant image groups.
      if (this.variantImages.length === 0 || !this.hasEnoughVariantImageGroups()) {
        this.showAllImages();
        return;
      }

      // Check if subscribe function exists
      if (typeof subscribe !== 'function' || !theme || !theme.PUB_SUB_EVENTS) {
        // Fallback: use direct event listeners
        this.setupDirectListeners();
        return;
      }

      // Listen for variant changes
      try {
        this.unsubscriber = subscribe(theme.PUB_SUB_EVENTS.variantChange, this.handleVariantChange.bind(this));
        
        // Also listen for initial variant selection
        this.unsubscriberOptionChange = subscribe(theme.PUB_SUB_EVENTS.optionValueSelectionChange, this.handleOptionChange.bind(this));
      } catch (e) {
        this.setupDirectListeners();
      }
      
      // Filter on initial load
      setTimeout(() => {
        this.filterImages();
      }, 200);
    }

    setupDirectListeners() {
      // Fallback: listen directly to variant-selects changes
      const variantSelects = this.productInfo.querySelector('variant-selects');
      if (variantSelects) {
        // Use MutationObserver to watch for changes in the selected variant script
        const observer = new MutationObserver(() => {
          setTimeout(() => {
            this.filterImages();
          }, 100);
        });

        const selectedVariantScript = variantSelects.querySelector('script[data-selected-variant]');
        if (selectedVariantScript) {
          observer.observe(selectedVariantScript, {
            childList: true,
            characterData: true,
            subtree: true
          });
          this.mutationObserver = observer;
        }

        // Also listen for clicks on variant options
        variantSelects.addEventListener('change', () => {
          setTimeout(() => {
            this.filterImages();
          }, 100);
        });
      }
    }

    handleVariantChange(payload) {
      const data = payload && payload.data;
      if (!data || !data.variant) return;
      // Only react when the event is for this product section (e.g. after navigating to another product)
      const sectionId = this.productInfo.getAttribute('data-section-id') || this.productInfo.dataset.section;
      if (data.sectionId != null && sectionId != null && String(data.sectionId) !== String(sectionId)) {
        return;
      }
      if (!this.productInfo.isConnected) return;
      this.filterImages(data.variant);
    }

    handleOptionChange({ data }) {
      if (!data || !data.target) return;
      if (!this.productInfo.isConnected || !this.productInfo.contains(data.target)) return;
      
      setTimeout(() => {
        if (!this.productInfo.isConnected) return;
        this.filterImages();
      }, 100);
    }

    getSelectedVariantTitle() {
      // Try to get variant from variant-selects component
      const variantSelects = this.productInfo.querySelector('variant-selects');
      if (variantSelects) {
        // data-selected-variant is inside a script tag
        const selectedVariantScript = variantSelects.querySelector('script[data-selected-variant]');
        if (selectedVariantScript) {
          try {
            const variant = JSON.parse(selectedVariantScript.textContent || selectedVariantScript.innerHTML);
            if (variant && variant.title) {
              return variant.title.trim();
            }
          } catch (e) {
          }
        }
      }

      // Fallback: try to get from product-info's getSelectedVariant method
      if (this.productInfo.getSelectedVariant) {
        try {
          const variant = this.productInfo.getSelectedVariant(this.productInfo);
          if (variant && variant.title) {
            return variant.title.trim();
          }
        } catch (e) {
        }
      }

      return null;
    }

    filterImages(variant = null) {
      const productMediaList = this.getMediaList();
      if (!productMediaList) return;

      const variantTitle = variant ? variant.title : this.getSelectedVariantTitle();
      
      
      // If no variant title found, show all images
      if (!variantTitle) {
        this.showAllImages();
        return;
      }

      const normalizedVariantTitle = variantTitle.trim();
      const normalizedVariantKey = normalizeVariantValue(normalizedVariantTitle);
      let hasMatchingImages = false;
      

      // Always use current DOM: refresh slides from the media list that belongs to this product
      this.allMediaSlides = Array.from(productMediaList.querySelectorAll('[data-slide]'));
      
      // Rebuild variantImages and genericImages arrays to catch any new slides
      this.variantImages = [];
      this.genericImages = [];
      this.allMediaSlides.forEach(slide => {
        const variantTitleAttr = slide.getAttribute('data-variant-title');
        if (variantTitleAttr) {
          const trimmed = variantTitleAttr.trim();
          if (trimmed.toLowerCase() === 'all') {
            this.genericImages.push(slide);
          } else {
            this.variantImages.push({
              slide: slide,
              variantTitle: trimmed
            });
          }
        } else {
          this.genericImages.push(slide);
        }
      });

      if (this.variantImages.length === 0 || !this.hasEnoughVariantImageGroups()) {
        this.showAllImages();
        return;
      }

      // CRITICAL: First, hide ALL slides (both variant-specific and generic)
      this.allMediaSlides.forEach(slide => {
        slide.classList.add('media--hidden');
        slide.classList.remove('media--active', 'media--hiding', 'first-visible');
        slide.removeAttribute('data-variant-filter-visible');
      });

      // Now show only matching variant images
      let firstMatch = true;
      this.variantImages.forEach(({ slide, variantTitle: slideVariantTitle }) => {
        const slideVariantKey = normalizeVariantValue(slideVariantTitle);
        const isMatch = slideVariantTitle === normalizedVariantTitle
          || slideVariantKey === normalizedVariantKey;

        if (isMatch) {
          slide.setAttribute('data-variant-filter-visible', 'true');
          // Only make the first matching image active
          if (firstMatch) {
            slide.classList.remove('media--hidden');
            slide.classList.add('media--active');
            firstMatch = false;
          }
          hasMatchingImages = true;
        }
        // All non-matching variant images remain hidden (already set above)
      });
      
      
      // If no matching variant images, show generic images only
      if (!hasMatchingImages && this.genericImages.length > 0) {
        this.genericImages.forEach((slide) => {
          slide.setAttribute('data-variant-filter-visible', 'true');
        });
      } else if (hasMatchingImages && this.genericImages.length > 0) {
        // When there ARE matching variant images, also show all generic/"all" images,
        // but keep the active state on the first variant-specific image
        this.genericImages.forEach(slide => {
          slide.setAttribute('data-variant-filter-visible', 'true');
        });
      }

      // Keep gallery deterministic after variant filter: start from the first visible slide.
      const visibleSlides = Array.from(productMediaList.querySelectorAll('[data-slide][data-variant-filter-visible="true"]'));
      const nextActive = visibleSlides[0] || null;
      
      // Remove first-visible from all slides
      this.allMediaSlides.forEach(slide => {
        slide.classList.remove('first-visible');
      });
      
      const isMobileFader = this.isMobileFaderMode();
      visibleSlides.forEach((slide, index) => {
        if (index === 0) {
          slide.classList.add('first-visible');
        }

        if (!isMobileFader) {
          // Desktop/stacked mode: unhide all filtered slides.
          slide.classList.remove('media--hidden');
          if (nextActive && slide === nextActive) {
            slide.classList.add('media--active');
          } else {
            slide.classList.remove('media--active');
          }
        } else {
          // Mobile fader mode: keep only one visible slide, but keep all filtered slides in swipe set.
          if (nextActive && slide === nextActive) {
            slide.classList.remove('media--hidden');
            slide.classList.add('media--active');
          } else {
            if (this.isSlideInFilteredSet(slide)) {
              slide.classList.add('media--hidden');
            }
            slide.classList.remove('media--active');
          }
        }
      });

      this.syncFloatingVideo(nextActive);

      // Update thumbnails visibility
      this.updateThumbnails();

      // Sync active media id for thumbnails/dots and keep first visible stable after variant updates.
      const firstVisible = productMediaList.querySelector('[data-slide][data-variant-filter-visible="true"]');
      const firstVisibleId = firstVisible ? firstVisible.getAttribute('data-media-id') : null;
      if (firstVisibleId) {
        const productImages = this.productInfo.querySelector('product-images');
        if (productImages) {
          this.setProductImagesActiveMedia(productImages, firstVisibleId);
          this.stabilizeFirstVisibleMedia(productImages, firstVisibleId);
        }
      }
      
      // Final verification log
      const finalVisible = productMediaList.querySelectorAll('[data-slide]:not(.media--hidden)');
      const finalHidden = productMediaList.querySelectorAll('[data-slide].media--hidden');
    }

    showAllImages() {
      const productMediaList = this.getMediaList();
      if (!productMediaList) return;

      const slides = Array.from(productMediaList.querySelectorAll('[data-slide]'));
      const isMobileFader = this.isMobileFaderMode();
      slides.forEach(slide => {
        slide.classList.remove('media--hiding', 'first-visible');
        slide.setAttribute('data-variant-filter-visible', 'true');
        if (!isMobileFader) {
          slide.classList.remove('media--hidden');
        }
      });

      const nextActive = slides[0];
      slides.forEach((slide, index) => {
        if (index === 0) {
          slide.classList.add('first-visible');
        }

        if (!isMobileFader) {
          if (nextActive && slide === nextActive) {
            slide.classList.add('media--active');
          } else {
            slide.classList.remove('media--active');
          }
        } else {
          if (nextActive && slide === nextActive) {
            slide.classList.remove('media--hidden');
            slide.classList.add('media--active');
          } else {
            slide.classList.add('media--hidden');
            slide.classList.remove('media--active');
          }
        }
      });

      this.syncFloatingVideo(nextActive);

      this.updateThumbnails();

      const productImages = this.productInfo.querySelector('product-images');
      const firstVisible = slides[0];
      const firstVisibleId = firstVisible ? firstVisible.getAttribute('data-media-id') : null;
      if (productImages && firstVisibleId) {
        this.setProductImagesActiveMedia(productImages, firstVisibleId);
      }
    }

    updateThumbnails() {
      const productMediaList = this.getMediaList();
      const productThumbs = this.getThumbs();
      if (!productThumbs || !productMediaList) return;

      const thumbLinks = productThumbs.querySelectorAll('[data-thumb-link]');
      const visibleSlides = Array.from(productMediaList.querySelectorAll('[data-slide][data-variant-filter-visible="true"]'));
      const visibleMediaIds = visibleSlides
        .filter((slide) => this.isSlideInFilteredSet(slide))
        .map(slide => slide.getAttribute('data-media-id'));

      thumbLinks.forEach(thumbLink => {
        const mediaId = thumbLink.getAttribute('data-media-id');
        const thumbItem = thumbLink.closest('[data-thumb-item]');
        
        if (visibleMediaIds.includes(mediaId)) {
          thumbItem.style.display = '';
        } else {
          thumbItem.style.display = 'none';
        }
      });
    }

    setProductImagesActiveMedia(productImages, mediaId) {
      if (!productImages || !mediaId) return;

      const useThemeFaderSetter = Boolean(theme?.isMobile && productImages.hasAttribute('data-fader-mobile'));

      if (useThemeFaderSetter && typeof productImages.setActiveMedia === 'function') {
        productImages.setActiveMedia(mediaId);
      } else {
        productImages.setAttribute('data-active-media', mediaId);
        const active = productImages.querySelector('[data-image-id].media--active');
        const next = productImages.querySelector(`[data-media-id="${mediaId}"]`);

        // Desktop stacked layout: keep all currently visible slides visible, only change active marker.
        active?.classList.remove('media--active', 'media--hiding');
        next?.classList.remove('media--hiding');
        next?.classList.add('media--active');
      }
    }

    stabilizeFirstVisibleMedia(productImages, mediaId) {
      const shouldStabilize = Boolean(theme?.isMobile && productImages.hasAttribute('data-fader-mobile'));
      if (!shouldStabilize) return;

      // Cancel older stabilizers to avoid stale re-application across fast variant changes.
      this.activeMediaStabilizerTimers.forEach((timerId) => clearTimeout(timerId));
      this.activeMediaStabilizerTimers = [];

      // Theme's variant update can fire shortly after our filter and force featured media.
      // Re-apply the first visible media a few times in a short window, then stop.
      [60, 180, 360, 650].forEach((delay) => {
        const timerId = setTimeout(() => {
          if (!this.productInfo.isConnected) return;
          const mediaList = this.getMediaList();
          if (!mediaList) return;
          const firstVisible = mediaList.querySelector('[data-slide][data-variant-filter-visible="true"]');
          if (!firstVisible) return;
          const firstVisibleId = firstVisible.getAttribute('data-media-id');
          if (!firstVisibleId) return;
          this.setProductImagesActiveMedia(productImages, firstVisibleId);
        }, delay);

        this.activeMediaStabilizerTimers.push(timerId);
      });
    }

    destroy() {
      if (this.unsubscriber) {
        this.unsubscriber();
      }
      if (this.unsubscriberOptionChange) {
        this.unsubscriberOptionChange();
      }
      if (this.mutationObserver) {
        this.mutationObserver.disconnect();
      }
      if (this.activeMediaStabilizerTimers.length) {
        this.activeMediaStabilizerTimers.forEach((timerId) => clearTimeout(timerId));
        this.activeMediaStabilizerTimers = [];
      }
    }
  }

  // Wait for product-info to be defined, then extend it
  function initializeVariantImageFiltering() {
    const ProductInfoElement = customElements.get('product-info');
    if (!ProductInfoElement) {
      // Wait a bit and try again
      setTimeout(initializeVariantImageFiltering, 100);
      return;
    }


    // Store original connectedCallback
    const originalConnectedCallback = ProductInfoElement.prototype.connectedCallback;
    
    ProductInfoElement.prototype.connectedCallback = function() {
      if (originalConnectedCallback) {
        originalConnectedCallback.call(this);
      }
      
      // Initialize variant image filtering after a short delay to ensure DOM is ready
      setTimeout(() => {
        if (!this.variantImageFilter) {
          try {
            this.variantImageFilter = new ProductVariantImages(this);
          } catch (e) {
          }
        }
      }, 200);
    };

    // Store original disconnectedCallback if it exists
    const originalDisconnectedCallback = ProductInfoElement.prototype.disconnectedCallback;
    
    ProductInfoElement.prototype.disconnectedCallback = function() {
      if (this.variantImageFilter) {
        this.variantImageFilter.destroy();
        this.variantImageFilter = null;
      }
      
      if (originalDisconnectedCallback) {
        originalDisconnectedCallback.call(this);
      }
    };

    // Also try to initialize for existing product-info elements
    const existingProductInfo = document.querySelector('product-info');
    if (existingProductInfo) {
      setTimeout(() => {
        if (!existingProductInfo.variantImageFilter) {
          try {
            existingProductInfo.variantImageFilter = new ProductVariantImages(existingProductInfo);
          } catch (e) {
          }
        }
      }, 300);
    }
  }

  // Start waiting for product-info element
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function() {
      waitForProductInfo();
    });
  } else {
    waitForProductInfo();
  }

  // Also try direct initialization after a delay as fallback
  setTimeout(function() {
    const productInfo = document.querySelector('product-info');
    if (productInfo && !productInfo.variantImageFilter) {
      try {
        productInfo.variantImageFilter = new ProductVariantImages(productInfo);
      } catch (e) {
      }
    }
  }, 1000);

})();